import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiFilter, ApiPage } from "@result/rui/utils/api";

import ApiClient from "#/redux/api/ApiClient";
import { getErrorMessage } from "#/utils/APIUtils";

const baseUrl = "/api/dog/kotitve/kotitev";

export const createLambing = createAsyncThunk(
  "lambing/create",
  async ({ dto }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post(baseUrl, dto);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updateLambing = createAsyncThunk(
  "lambing/update",
  async ({ id, dto }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.put(`${baseUrl}/${id}`, dto);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getLambing = createAsyncThunk(
  "lambing/get",
  async (id, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(`${baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getNumberOfAlreadyBornCubsForMother = createAsyncThunk(
  "lambing/getNumberOfAlreadyBornCubsForMother",
  async (motherId, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `${baseUrl}/page`,
        null,
        new ApiPage().page(0).size(100),
        new ApiFilter().equal("pripustSamica.samica.id", motherId),
        null,
        false
      );
      const lambingIds = response.data.content.map((lambing) => lambing.id);

      if (lambingIds.length === 0) {
        return {
          motherId: motherId,
          numberOfChildren: 0,
        };
      }

      const markingsResponse = await ApiClient.get(
        `/api/dog/oznacevanje/oznDrob/page`,
        undefined,
        new ApiPage().page(0).size(1000),
        new ApiFilter().in("kotitevId", lambingIds),
        null,
        false
      );

      return {
        motherId: motherId,
        numberOfChildren: markingsResponse.data.content?.length || 0,
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getLambingPage = createAsyncThunk(
  "lambing/page",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `${baseUrl}/page`,
        undefined,
        page,
        filter,
        sort,
        false
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchLatestLambingRecordForAnimal = createAsyncThunk(
  "lambing/fetchLatestForAnimal",
  async (animalId, { rejectWithValue }) => {
    try {
      // Step 1: Get pripustSamice records for this animal
      const pripustSamiceResponse = await ApiClient.get(
        "/api/dog/pripusti/pripustSamice/page",
        undefined,
        { size: 1 },
        new ApiFilter().equal("samica.id", animalId),
        "sort=datumPrihoda,desc",
        false
      );

      const latestPripustSamica = pripustSamiceResponse.data.content?.[0];

      if (!latestPripustSamica?.id) {
        return null;
      }

      // Step 2: Get kotitev records using pripustSamica.id
      const kotitevResponse = await ApiClient.get(
        `${baseUrl}/page`,
        undefined,
        { size: 1 },
        new ApiFilter().equal("pripustSamica.id", latestPripustSamica.id),
        "sort=datumKotitve,desc",
        false
      );

      return kotitevResponse.data.content?.[0] || null;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const findLambingFather = createAsyncThunk(
  "lambing/findFather",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        "/api/zival/zivalaggregate/page",
        undefined,
        page,
        filter,
        sort,
        false
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const lambingSlice = createSlice({
  name: "lambing",
  initialState: {
    lambing: null,
    page: {},
    fatherPage: {},
    totalCubsForMotherId: {},
    byId: {},
    latestRecord: null,
    error: null,
    isLoading: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createLambing.fulfilled, (state, action) => {
        state.isLoading = false;
        state.lambing = action.payload;
      })
      .addCase(createLambing.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createLambing.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateLambing.fulfilled, (state, action) => {
        state.isLoading = false;
        state.lambing = action.payload;
      })
      .addCase(updateLambing.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateLambing.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getLambing.fulfilled, (state, action) => {
        state.isLoading = false;
        state.lambing = action.payload;
        state.byId[action.payload.id] = action.payload;
      })
      .addCase(getLambing.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getLambing.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getLambingPage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.page = action.payload;
      })
      .addCase(getLambingPage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getLambingPage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLatestLambingRecordForAnimal.fulfilled, (state, action) => {
        state.isLoading = false;
        state.latestRecord = action.payload;
      })
      .addCase(fetchLatestLambingRecordForAnimal.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchLatestLambingRecordForAnimal.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(findLambingFather.fulfilled, (state, action) => {
        state.isLoading = false;
        state.fatherPage = action.payload;
      })
      .addCase(findLambingFather.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(findLambingFather.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        getNumberOfAlreadyBornCubsForMother.fulfilled,
        (state, action) => {
          state.totalCubsForMotherId[action.payload.motherId] =
            action.payload.numberOfChildren;
        }
      );
  },
});

export default lambingSlice.reducer;
